import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Package2, ChevronDown, AlertTriangle, ShoppingCart, Search, Trash2, Calendar } from 'lucide-react';
import { StockItem } from '../lib/stockItems';
import { getSuppliers, Customer } from '../lib/customers';
import { useBusiness } from '../contexts/BusinessContext';
import OrderSummaryPDFModal from './OrderSummaryPDFModal';
import { createOrder, OrderItem as DBOrderItem, getPendingOrdersForItems } from '../lib/orders';
import { toast } from '../components/toasts/SimpleToast';

interface PlaceOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  items: StockItem[];
}

interface OrderItem {
  item: StockItem;
  quantityToOrder: number;
  totalCost: number;
}

interface SupplierOrder {
  supplier: Customer;
  items: OrderItem[];
  totalCost: number;
  itemCount: number;
}

export default function PlaceOrderModal({
  isOpen,
  onClose,
  items
}: PlaceOrderModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<Customer[]>([]);
  const [supplierOrders, setSupplierOrders] = useState<SupplierOrder[]>([]);
  const [expandedSuppliers, setExpandedSuppliers] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<{
    supplierId: string;
    supplier: Customer;
    items: OrderItem[];
    totalAmount: number;
  } | null>(null);
  const [pendingOrderItems, setPendingOrderItems] = useState<Map<string, number>>(new Map());
  const [removedItems, setRemovedItems] = useState<Set<string>>(new Set());
  
  // Load suppliers
  const loadSuppliers = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      setLoading(true);
      const data = await getSuppliers(selectedBusiness.id);
      setSuppliers(data);
      setError(null);
    } catch (err) {
      console.error('Error loading suppliers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness?.id]);
  
  // Load pending orders
  const loadPendingOrders = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const pendingItems = await getPendingOrdersForItems(selectedBusiness.id);
      
      // Convert to a Map for easy lookup
      const itemMap = new Map<string, number>();
      pendingItems.forEach(item => {
        itemMap.set(item.itemId, item.quantityOrdered);
      });
      
      setPendingOrderItems(itemMap);
      
    } catch (err) {
      console.error('Error loading pending orders:', err);
      // Don't set error here to avoid blocking the UI
    }
  }, [selectedBusiness?.id]);
  
  // Calculate items that need to be ordered (below reorder point)
  const calculateOrderItems = useCallback(() => {
    // Filter items that are below reorder point and have a supplier
    // Also exclude items that are already in pending orders
    const itemsToOrder = items.filter(item => {
      // First check if item is deleted
      if (item.is_deleted) return false;
      
      // Skip items that were manually removed from this order session
      if (removedItems.has(item.id)) return false;

      // Check basic conditions
      const isLowStock = item.quantity <= item.reorder_point;
      const hasSupplier = !!item.supplier_id;
      const canAddMoreStock = item.max_quantity > item.quantity;
      
      // Check if the item is already in a pending order
      const pendingQuantity = pendingOrderItems.get(item.id) || 0;
      const projectedQuantity = item.quantity + pendingQuantity;
      
      // If the projected quantity after all pending orders are received
      // will be above the reorder point, don't include it
      const stillNeedsOrdering = projectedQuantity <= item.reorder_point;
      
      return isLowStock && hasSupplier && canAddMoreStock && stillNeedsOrdering;
    });
    
    // Group by supplier
    const ordersBySupplier: Record<string, SupplierOrder> = {};
    
    itemsToOrder.forEach(item => {
      if (!item.supplier_id) return; // Skip items without supplier
      
      const supplier = suppliers.find(s => s.id === item.supplier_id);
      if (!supplier) return; // Skip if supplier not found
      
      // Calculate how much quantity to order considering pending orders
      const pendingQuantity = pendingOrderItems.get(item.id) || 0;
      const effectiveQuantity = item.quantity + pendingQuantity;
      const quantityToOrder = item.max_quantity - effectiveQuantity;
      
      // Only proceed if we actually need to order more
      if (quantityToOrder <= 0) return;
      
      const totalCost = quantityToOrder * item.acquisition_cost;
      
      if (!ordersBySupplier[supplier.id]) {
        ordersBySupplier[supplier.id] = {
          supplier,
          items: [],
          totalCost: 0,
          itemCount: 0
        };
      }
      
      ordersBySupplier[supplier.id].items.push({
        item,
        quantityToOrder,
        totalCost
      });
      
      ordersBySupplier[supplier.id].totalCost += totalCost;
      ordersBySupplier[supplier.id].itemCount += 1;
    });
    
    // Convert to array and sort by supplier name
    const ordersArray = Object.values(ordersBySupplier)
      .sort((a, b) => {
        const aName = `${a.supplier.first_name} ${a.supplier.last_name}`.trim();
        const bName = `${b.supplier.first_name} ${b.supplier.last_name}`.trim();
        return aName.localeCompare(bName);
      });
    
    setSupplierOrders(ordersArray);
  }, [items, suppliers, pendingOrderItems, removedItems]);
  
  // Toggle supplier expansion
  const toggleSupplier = (supplierId: string) => {
    setExpandedSuppliers(prev => {
      const next = new Set(prev);
      if (next.has(supplierId)) {
        next.delete(supplierId);
      } else {
        next.add(supplierId);
      }
      return next;
    });
  };
  
  // Update order quantity for an item
  const updateOrderQuantity = (supplierId: string, itemId: string, quantity: number) => {
    setSupplierOrders(prevOrders => {
      return prevOrders.map(order => {
        if (order.supplier.id !== supplierId) return order;
        
        const updatedItems = order.items.map(orderItem => {
          if (orderItem.item.id !== itemId) return orderItem;
          
          const newQuantity = Math.max(1, quantity); // Ensure at least 1
          const newTotalCost = newQuantity * orderItem.item.acquisition_cost;
          
          return {
            ...orderItem,
            quantityToOrder: newQuantity,
            totalCost: newTotalCost
          };
        });
        
        // Calculate new total cost
        const newTotalCost = updatedItems.reduce((sum, item) => sum + item.totalCost, 0);
        
        return {
          ...order,
          items: updatedItems,
          totalCost: newTotalCost
        };
      });
    });
  };
  
  // Handle placing an order
  const handlePlaceOrder = (supplierId: string) => {
    const orderGroup = supplierOrders.find(o => o.supplier.id === supplierId);
    if (!orderGroup) {
      console.error('Order group not found for supplier', supplierId);
      return;
    }
    
    setSelectedOrder({
      supplierId,
      supplier: orderGroup.supplier,
      items: orderGroup.items,
      totalAmount: orderGroup.totalCost
    });
  };
  
  // Function to handle sending the order
  const handleSendOrder = async () => {
    if (!selectedOrder) return false;
    
    try {
      setLoading(true);
      
      // Create order number and get current date
      const orderNumber = `PO-${Date.now().toString().slice(-6)}`;
      
      if (!selectedBusiness?.id) {
        throw new Error('No business selected');
      }
      
      // Prepare order items for the database
      const dbOrderItems: DBOrderItem[] = selectedOrder.items.map(item => ({
        item_id: item.item.id,
        quantity: item.quantityToOrder,
        unit_price: item.item.acquisition_cost,
        total_price: item.totalCost,
        item_name: item.item.name,
        item_unit: item.item.unit
      }));
      
      // Create the order in the database
      await createOrder(
        {
          business_id: selectedBusiness.id,
          supplier_id: selectedOrder.supplier.id,
          order_number: orderNumber,
          total_amount: selectedOrder.totalAmount,
          status: 'pending'
        },
        dbOrderItems
      );
      
      // Show success notification
      toast.success({
        title: "Order Placed",
        description: `Order ${orderNumber} has been created and sent to ${selectedOrder.supplier.first_name} ${selectedOrder.supplier.last_name}.`,
        duration: 5000,
      });
      
      // Refresh pending orders
      await loadPendingOrders();
      
      // After the order is successfully sent, update the supplier orders
      // by removing the supplier that was just ordered from
      setSupplierOrders(prevOrders => 
        prevOrders.filter(order => order.supplier.id !== selectedOrder.supplierId)
      );
      
      // Clear any expanded state for this supplier
      setExpandedSuppliers(prev => {
        const next = new Set(prev);
        next.delete(selectedOrder.supplierId);
        return next;
      });
      
      // Do NOT add the items to the removedItems set,
      // as we want them to show up next time the modal is opened
      // if they still need to be ordered
      
      return true;
    } catch (err) {
      console.error('Error sending order:', err);
      setError(err instanceof Error ? err.message : 'Failed to send order');
      
      // Show error notification
      toast.error({
        title: "Order Failed",
        description: err instanceof Error ? err.message : 'Failed to send order',
        duration: 5000,
      });
      
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  // Close the order summary modal
  const handleCloseOrderSummary = () => {
    setSelectedOrder(null);
  };
  
  // Initialize when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadSuppliers();
      loadPendingOrders();
      setRemovedItems(new Set()); // Reset removed items when modal is opened
    }
  }, [isOpen, loadSuppliers, loadPendingOrders]);
  
  // Calculate orders after suppliers and pending orders are loaded
  useEffect(() => {
    if (suppliers.length > 0) {
      calculateOrderItems();
    }
  }, [suppliers, pendingOrderItems, calculateOrderItems]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  // Filter orders based on search query
  const filteredSupplierOrders = searchQuery 
    ? supplierOrders.filter(order => {
        const supplierName = `${order.supplier.first_name} ${order.supplier.last_name}`.toLowerCase();
        const supplierEmail = order.supplier.email?.toLowerCase() || '';
        return supplierName.includes(searchQuery.toLowerCase()) ||
               supplierEmail.includes(searchQuery.toLowerCase()) ||
               order.items.some(item => 
                 item.item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                 item.item.description?.toLowerCase().includes(searchQuery.toLowerCase())
               );
      })
    : supplierOrders;

  // Handle removing an item from the order
  const handleRemoveItem = (supplierId: string, itemId: string) => {
    // Add the item to the removed items set
    setRemovedItems(prev => {
      const updated = new Set(prev);
      updated.add(itemId);
      return updated;
    });
    
    // Update supplier orders by removing the item
    setSupplierOrders(prevOrders => {
      return prevOrders.map(order => {
        if (order.supplier.id !== supplierId) return order;
        
        const updatedItems = order.items.filter(orderItem => orderItem.item.id !== itemId);
        
        // Calculate new total cost
        const newTotalCost = updatedItems.reduce((sum, item) => sum + item.totalCost, 0);
        
        // Return updated order or null if no items left
        return {
          ...order,
          items: updatedItems,
          totalCost: newTotalCost,
          itemCount: updatedItems.length
        };
      }).filter(order => order.items.length > 0); // Remove suppliers with no items left
    });
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <>
      <div 
        className={`fixed inset-0 bg-black transition-opacity duration-300 ${
          isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
        } z-[9999]`}
        onClick={onClose}
        style={{
          touchAction: 'manipulation',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        }}
      >
        <div 
          className={`fixed inset-y-0 right-0 w-full md:w-[700px] backdrop-blur-xl bg-white/80 transform transition-transform duration-300 ease-out shadow-2xl ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          style={{ 
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)'
          }}
          onTransitionEnd={handleTransitionEnd}
          onClick={e => e.stopPropagation()}
        >
          <div className="h-full flex flex-col">
            {/* Header with Mobile-Optimized Design */}
            <div className="relative bg-gray-800 border-b border-gray-700 shadow-lg">
              <div className="flex items-center justify-between px-2 py-2 sm:px-6 sm:py-4">
                <div className="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
                  <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center shadow-sm">
                    <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base sm:text-lg font-bold text-white truncate">
                      Place Order
                    </h2>
                    <div className="text-xs sm:text-sm text-gray-300 font-medium">
                      Create supply orders for low stock items
                    </div>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="flex items-center justify-center w-8 h-8 sm:w-11 sm:h-11 rounded-lg sm:rounded-full bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 touch-manipulation shadow-sm"
                  style={{ minWidth: '32px', minHeight: '32px' }}
                >
                  <X className="h-4 w-4 sm:h-6 sm:w-6" />
                </button>
              </div>
            </div>

            {/* Search Section */}
            <div className="backdrop-blur-xl bg-white/70 border-b border-purple-200/30 p-4 sm:p-6"
                 style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-purple-400" />
                <input
                  type="text"
                  placeholder="Search items or suppliers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-purple-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                  style={{ 
                    minHeight: '44px',
                    fontSize: '16px',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)'
                  }}
                />
              </div>
            </div>

            {/* Content Section */}
            <div className="flex-1 overflow-y-auto p-2 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="relative mx-auto w-16 h-16">
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full animate-spin">
                        <div className="absolute inset-2 bg-white/20 backdrop-blur-sm rounded-full"></div>
                      </div>
                    </div>
                    <p className="mt-6 text-lg font-medium text-gray-700">Loading orders...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="backdrop-blur-xl bg-red-50/90 border border-red-200/50 text-red-700 px-4 py-3 rounded-2xl shadow-lg"
                     style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                  <p className="text-sm">{error}</p>
                  <button 
                    onClick={loadSuppliers}
                    className="mt-2 text-sm font-medium text-red-600 hover:text-red-800"
                  >
                    Try again
                  </button>
                </div>
              ) : filteredSupplierOrders.length === 0 ? (
                <div className="text-center py-12">
                  <div className="relative mx-auto w-24 h-24 mb-6">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full opacity-20"></div>
                    <div className="absolute inset-2 bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center">
                      <Package2 className="h-10 w-10 text-purple-600" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">No items to order</h3>
                  <p className="text-gray-600">
                    {searchQuery 
                      ? "No items match your search. Try a different search term." 
                      : "All items are above their reorder point."}
                  </p>
                  
                  {/* Show items skipped because of pending orders */}
                  {pendingOrderItems.size > 0 && (
                    <div className="mt-8 text-left backdrop-blur-xl bg-blue-50/90 border border-blue-200/50 rounded-2xl p-4"
                         style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                      <h4 className="text-sm font-medium text-blue-800 mb-2">Items already in pending orders</h4>
                      <p className="text-xs text-blue-700 mb-3">
                        The following items have pending orders and don't need to be reordered at this time.
                      </p>
                      <div className="max-h-40 overflow-y-auto">
                        <ul className="space-y-1">
                          {items
                            .filter(item => !item.is_deleted && pendingOrderItems.has(item.id) && item.quantity <= item.reorder_point)
                            .map(item => (
                              <li key={item.id} className="flex justify-between text-xs text-gray-700 p-2 bg-white/80 backdrop-blur-sm rounded border border-blue-100">
                                <span>{item.name}</span>
                                <span className="font-medium">{pendingOrderItems.get(item.id) || 0} {item.unit}(s) ordered</span>
                              </li>
                            ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Show count of items skipped due to pending orders if any */}
                  {pendingOrderItems.size > 0 && items.some(item => 
                    !item.is_deleted && pendingOrderItems.has(item.id) && item.quantity <= item.reorder_point
                  ) && (
                    <div className="mb-4 backdrop-blur-xl bg-blue-50/90 border border-blue-200/50 rounded-2xl p-3"
                         style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                      <p className="text-xs text-blue-800 flex items-center">
                        <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />
                        {items.filter(item => 
                          !item.is_deleted && pendingOrderItems.has(item.id) && item.quantity <= item.reorder_point
                        ).length} items are below reorder point but already have pending orders
                      </p>
                    </div>
                  )}
                  
                  {filteredSupplierOrders.map((order) => (
                    <div 
                      key={order.supplier.id} 
                      className="backdrop-blur-xl bg-white/70 rounded-2xl border border-purple-200/30 overflow-hidden shadow-lg"
                      style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
                    >
                      <div 
                        className="bg-purple-50/50 backdrop-blur-sm p-4 flex items-center justify-between cursor-pointer"
                        style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                        onClick={() => toggleSupplier(order.supplier.id)}
                      >
                        <div>
                          <div className="flex items-center">
                            <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-sm font-bold text-white shadow-sm mr-3">
                              {order.supplier.first_name[0]}{order.supplier.last_name[0]}
                            </div>
                            <div>
                              <h3 className="text-sm font-bold text-gray-900">
                                {order.supplier.first_name} {order.supplier.last_name}
                                {order.supplier.email && (
                                  <span className="text-gray-500 ml-2 font-medium">
                                    ({order.supplier.email})
                                  </span>
                                )}
                              </h3>
                              <div className="mt-1 flex items-center space-x-2">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100/80 text-red-800 backdrop-blur-sm">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  Low Stock
                                </span>
                                <span className="text-xs text-gray-500">
                                  {order.itemCount} item{order.itemCount !== 1 ? 's' : ''} • 
                                  Total: ${order.totalCost.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handlePlaceOrder(order.supplier.id);
                            }}
                            className="mr-4 px-4 py-2 text-xs font-bold text-white bg-purple-600 rounded-2xl hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500/20 shadow-lg transition-all duration-300 ease-out transform hover:scale-105 active:scale-95"
                          >
                            <ShoppingCart className="h-3.5 w-3.5 inline-block mr-1.5" />
                            Place Order
                          </button>
                          <ChevronDown
                            className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                              expandedSuppliers.has(order.supplier.id) ? 'rotate-180' : ''
                            }`}
                          />
                        </div>
                      </div>
                      
                      {expandedSuppliers.has(order.supplier.id) && (
                        <div className="divide-y divide-purple-100/50">
                          {order.items.map(({ item, quantityToOrder, totalCost }) => (
                            <div key={item.id} className="p-4">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h4 className="text-sm font-bold text-gray-900">{item.name}</h4>
                                  {item.description && (
                                    <p className="mt-1 text-xs text-gray-600">{item.description}</p>
                                  )}
                                  <div className="mt-2 flex items-center text-xs text-gray-500 space-x-4">
                                    <span className="font-medium text-red-600 bg-red-50/50 px-2 py-1 rounded-lg">
                                      Current: {item.quantity} {item.unit}
                                    </span>
                                    <span className="bg-purple-50/50 px-2 py-1 rounded-lg">
                                      Max: {item.max_quantity} {item.unit}
                                    </span>
                                  </div>
                                </div>
                                <div className="text-right ml-4">
                                  <div className="flex justify-end mb-2">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveItem(order.supplier.id, item.id);
                                      }}
                                      className="p-2 text-red-500 hover:text-white bg-red-100/50 hover:bg-red-500 
                                               rounded-xl transition-all duration-200 backdrop-blur-sm
                                               hover:shadow-lg hover:scale-110"
                                      title="Remove from order"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                  <p className="text-sm font-bold text-gray-900">${totalCost.toFixed(2)}</p>
                                  <p className="text-xs text-gray-500 mb-2">
                                    @ ${item.acquisition_cost.toFixed(2)} per {item.unit}
                                  </p>
                                  <div className="flex items-center space-x-2">
                                    <input
                                      type="number"
                                      min="1"
                                      value={quantityToOrder}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => 
                                        updateOrderQuantity(
                                          order.supplier.id,
                                          item.id,
                                          parseInt(e.target.value) || 1
                                        )
                                      }
                                      className="w-16 px-2 py-1 text-sm bg-white/80 backdrop-blur-sm border border-purple-300/50 rounded-lg"
                                    />
                                    <span className="text-xs text-gray-500 font-medium">{item.unit}(s)</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                          <div className="backdrop-blur-sm bg-purple-50/50 p-4"
                               style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                            <div className="flex justify-between items-center">
                              <p className="text-sm font-bold text-gray-700">Order Total</p>
                              <p className="text-lg font-bold text-purple-700">${order.totalCost.toFixed(2)}</p>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePlaceOrder(order.supplier.id);
                              }}
                              className="mt-3 w-full py-3 text-sm font-bold text-white bg-purple-600 rounded-2xl hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-lg md:hidden"
                            >
                              <ShoppingCart className="h-4 w-4 inline-block mr-2" />
                              Place Order
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Render the OrderSummaryPDFModal when an order is selected */}
      {selectedOrder && (
        <OrderSummaryPDFModal
          isOpen={!!selectedOrder}
          onClose={handleCloseOrderSummary}
          supplier={selectedOrder.supplier}
          items={selectedOrder.items}
          totalAmount={selectedOrder.totalAmount}
          onSendOrder={handleSendOrder}
        />
      )}
    </>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return createPortal(modalContent, document.body);
} 